import React from "react";
import { TableHead, Skeleton, TableCell, TableRow } from "@mui/material";
import { TableSegment } from "../../utils/segmentationTableUtils";
import { AnalysisDriverType } from "../../types/types";
import HeaderRow from "./SegmentationHeaderRow";
import colors from "assets/theme-dark/base/colors";
import SubHeaderRow from "./SegmentationSubHeaderRow";

interface SegmentationTableHeaderProps {
  segments: TableSegment[];
  selectableHeader1: string;
  selectableHeader2: string;
  headerOptions: string[];
  setSelectableHeader1: (header: string) => void;
  setSelectableHeader2: (header: string) => void;
  driver1Type: string;
  driver2Type: string;
  driver1Mode: AnalysisDriverType;
  driver2Mode: AnalysisDriverType;
  driver1NumeratorType: string;
  driver2NumeratorType: string;
  tableName: string;
  sortColumn: string;
  sortDirection: "asc" | "desc";
  handleSort: (column: string) => void;
  hasOneDataAndOneManualSegment: boolean;
  isSkeleton: boolean;
}

const SegmentationTableHeader: React.FC<SegmentationTableHeaderProps> = ({
  segments,
  selectableHeader1,
  selectableHeader2,
  headerOptions,
  setSelectableHeader1,
  setSelectableHeader2,
  driver1Type,
  driver2Type,
  driver1Mode,
  driver2Mode,
  driver1NumeratorType,
  driver2NumeratorType,
  tableName,
  sortColumn,
  sortDirection,
  handleSort,
  hasOneDataAndOneManualSegment,
  isSkeleton,
}) => {
  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);
  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");
  const isDataOnly = segments.length === 1 && segments.every((seg) => seg.method === "DATA");

  if (isSkeleton) {
    const skeletonColumnCount = isManualOnly || isDataOnly || hasOneDataAndOneManualSegment ? 13 : 15;
    return (
      <TableHead>
        <TableRow>
          {Array(skeletonColumnCount)
            .fill(null)
            .map((_, index) => (
              <TableCell key={index} sx={{ border: `1px solid ${colors.grey[300]}`, padding: 1.5 }}>
                <Skeleton variant="rectangular" height={32} />
              </TableCell>
            ))}
        </TableRow>
      </TableHead>
    );
  }

  return (
    <TableHead>
      <HeaderRow
        segments={segments}
        selectableHeader1={selectableHeader1}
        selectableHeader2={selectableHeader2}
        headerOptions={headerOptions}
        setSelectableHeader1={setSelectableHeader1}
        setSelectableHeader2={setSelectableHeader2}
        driver1Type={driver1Type}
        driver2Type={driver2Type}
        driver1Mode={driver1Mode}
        driver2Mode={driver2Mode}
        driver1NumeratorType={driver1NumeratorType}
        driver2NumeratorType={driver2NumeratorType}
        hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
        isManualOnly={isManualOnly}
        isDataOnly={isDataOnly}
      />
      <SubHeaderRow
        segments={segments}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        handleSort={handleSort}
        hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
        isManualOnly={isManualOnly}
        isDataOnly={isDataOnly}
        isTwoSameMethod={isTwoSameMethod}
      />
    </TableHead>
  );
};

export default SegmentationTableHeader;
